import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { publicApi, memberApi } from "../lib/api";
import { useMemberAuth } from "../context/MemberAuthContext";
import { toast } from "../hooks/use-toast";
import { ArrowLeft, Calendar, Clock, MapPin, User, Lock, CheckCircle2, XCircle } from "lucide-react";

const WebinarDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { member } = useMemberAuth();
  const [w, setW] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      // Prefer member endpoint if signed in for state info
      if (member) {
        const { data } = await memberApi.get(`/member/events/${id}`);
        setW({ ...data, is_full: data.seats_available === 0 && (data.capacity || 0) > 0 });
      } else {
        const { data } = await publicApi.get(`/webinars/${id}`);
        setW(data);
      }
    } catch { setNotFound(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, member]);

  const reserve = async () => {
    if (!member) { nav(`/login?next=/webinars/${id}`); return; }
    setBusy(true);
    try {
      const { data } = await memberApi.post(`/member/events/${id}/reserve-v2`, {});
      if (data.waitlisted) toast({ title: 'Added to the waiting list' });
      else if (data.already) toast({ title: 'Already reserved' });
      else toast({ title: 'Seat reserved' });
      load();
    } catch (err) {
      toast({ title: 'Could not reserve', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setBusy(false); }
  };

  const release = async () => {
    setBusy(true);
    try { await memberApi.post(`/member/events/${id}/release`, {}); toast({ title: 'Seat released' }); load(); }
    catch { toast({ title: 'Could not release' }); }
    finally { setBusy(false); }
  };

  const confirmOffer = async () => {
    setBusy(true);
    try { await memberApi.post(`/member/events/${id}/confirm`, {}); toast({ title: 'Seat confirmed' }); load(); }
    catch (err) { toast({ title: 'Could not confirm', description: err?.response?.data?.detail || 'Try again.' }); }
    finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 text-center">
      <XCircle size={40} className="mx-auto text-[#e0684d] mb-4" />
      <h1 className="hero-title text-[36px]">Masterclass not found</h1>
      <div className="mt-6"><Link to="/webinars" className="link-underline text-[#e0684d]">Back to masterclasses</Link></div>
    </div>
  );
  if (!w) return <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16">Loading…</div>;

  const primaryButton = () => {
    if (!member) {
      return (
        <div className="flex items-center gap-3 flex-wrap" data-testid="webinar-non-member-cta">
          <Link to={`/login?next=/webinars/${id}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px]"><Lock size={14} /> Sign in to reserve</Link>
          <Link to="/join" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-green text-[14px]" data-testid="webinar-apply-cta">Apply to join</Link>
          <div className="w-full text-[12px] text-[#0d2a20]/60">Masterclass seats are reserved for Vemoye Diaspora Network members. Approved members can sign in; everyone else is welcome to apply.</div>
        </div>
      );
    }
    const state = w.my_state;
    if (state === 'confirmed' || (w.registered && !w.waitlisted)) return <button onClick={release} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-ghost text-[14px]">Release your seat</button>;
    if (state === 'offered') return <button onClick={confirmOffer} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px]"><CheckCircle2 size={14} /> Confirm your seat (24h)</button>;
    if (state === 'waitlisted') return <button onClick={release} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-ghost text-[14px]">Leave the waiting list</button>;
    if (w.is_full) return w.waitlist_enabled ? <button onClick={reserve} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-green text-[14px]">Join the Waiting List</button> : <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[14px]" style={{ background: '#f3d6d1', color: '#7a1f13' }}>Masterclass Full</span>;
    return <button onClick={reserve} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px]">Reserve a Seat</button>;
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-12">
      <Link to="/webinars" className="inline-flex items-center gap-1 text-[13px] link-underline mb-6"><ArrowLeft size={14} /> All masterclasses</Link>
      <div className="rounded-lg overflow-hidden" style={{ background: '#fbf8ef' }}>
        {w.image_url && (
          <div className="aspect-[21/9] overflow-hidden"><img src={w.image_url.startsWith('http') ? w.image_url : `${process.env.REACT_APP_BACKEND_URL}${w.image_url}`} alt="" className="w-full h-full object-cover" /></div>
        )}
        <div className="p-6 md:p-10">
          <div className="flex items-center gap-2 text-[12px] text-[#0d2a20]/60 mb-4 flex-wrap">
            <span className="px-2 py-1 rounded flex items-center gap-1" style={{ background: '#ece5d3' }}><Calendar size={12} /> {w.date}</span>
            <span className="inline-flex items-center gap-1"><Clock size={12} /> {w.start_time}–{w.end_time} {w.timezone}</span>
            <span className="inline-flex items-center gap-1"><MapPin size={12} /> {w.location}</span>
            {w.tag && <span>{w.tag}</span>}
          </div>
          <h1 className="hero-title text-[36px] md:text-[52px] leading-tight mb-4">{w.title}</h1>
          <p className="text-[15px] leading-relaxed text-[#0d2a20]/85 whitespace-pre-wrap max-w-[820px] mb-8">{w.description}</p>

          {(w.speaker_name || (w.speakers && w.speakers.length)) && (
            <div className="mb-8">
              <div className="section-eyebrow text-[#0d2a20]/60 mb-3">Speaker</div>
              <div className="rounded-md p-4 border flex items-start gap-4" style={{ borderColor: 'rgba(13,42,32,0.08)', background: 'white' }}>
                {w.speaker_photo_url ? (
                  <img src={w.speaker_photo_url.startsWith('http') ? w.speaker_photo_url : `${process.env.REACT_APP_BACKEND_URL}${w.speaker_photo_url}`} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: '#0f3d2e', color: '#f4efe4' }}><User size={22} /></div>
                )}
                <div>
                  <div className="text-[15px] font-medium">{w.speaker_name || w.speakers?.[0]?.name}</div>
                  {w.speaker_title && <div className="text-[12px] text-[#0d2a20]/70 mb-2">{w.speaker_title}</div>}
                  <p className="text-[13px] text-[#0d2a20]/85 leading-relaxed">{w.speaker_bio || w.speakers?.[0]?.bio}</p>
                </div>
              </div>
            </div>
          )}

          {w.joining_info && (
            <div className="mb-8">
              <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Joining information</div>
              <p className="text-[14px] text-[#0d2a20]/85 whitespace-pre-wrap">{w.joining_info}</p>
            </div>
          )}

          <div className="pt-6 border-t flex items-center gap-4 flex-wrap" style={{ borderColor: 'rgba(13,42,32,0.08)' }}>
            {primaryButton()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebinarDetail;
